import {DISTRIBUTION_MODE} from '@client/constants/liquidation';
import {getSortedContent} from '@client/util/contentful';
// eslint-disable-next-line import/no-cycle
import liquidationService from '@client/services/liquidation';
// eslint-disable-next-line import/no-cycle
import custodianService from '@client/services/custodian';
import transferStatus from '@client/enums/transferStatus';
import transferType from '@client/enums/transferType';
import {TRANSFERDIRECTIONID, TRANSFER_INITIATED_FROM_ID} from '@client/util/constants/transfers';
import format from 'date-fns/format';
import {DATE_FORMAT_DB} from '@client/util/constants';
import transferFrequency from '@client/enums/transferFrequency';
import {getDate} from '@client/util/dateHelper';
import parseISO from 'date-fns/parseISO';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import {Logger, Category} from '@simpleinvest/front-end-logger';

const logger = new Logger({
  category: Category.FullLiquidations,
  fileName: 'liquidations.js',
});

const DAYS_TO_COMPARE_SINCE_ADDRESS_UPDATED = 15;

const initialState = {
  selectedAccount: null,
  distributionMode: DISTRIBUTION_MODE.ACH,
  selectedBankLink: null,
  selectedBankLinkVerified: false,
  selectedAddressType: null,
  selectedCheckAddress: null,
  checkProcessingFee: 0,
  checkDeliveryType: null,
  taxInformation: {
    stateTax: 0,
    federalTax: 0,
    distributionFee: 0,
    distributionReason: null,
  },
  retirementAccount: false,
  iraConstraints: null,
  contentType: 'introductoryContent',
  liquidationLandingContent: [],
};
const getters = {
  selectedAccount: (state) => state.selectedAccount,
  distributionMode: (state) => state.distributionMode,
  selectedBankLink: (state) => state.selectedBankLink,
  selectedBankLinkVerified: (state) => state.selectedBankLinkVerified,
  selectedAddressType: (state) => state.selectedAddressType,
  selectedCheckAddress: (state) => state.selectedCheckAddress,
  checkProcessingFee: (state) => state.checkProcessingFee,
  checkDeliveryType: (state) => state.checkDeliveryType,
  taxInformation: (state) => state.taxInformation,
  retirementAccount: (state) => state.retirementAccount,
  iraConstraints: (state) => state.iraConstraints,
  liquidationLandingContent: (state) => state.liquidationLandingContent,
  addressUpdatedDiff: (_state, _getters, rootState) => {
    let primaryAddressUpdatedDaysDiff = DAYS_TO_COMPARE_SINCE_ADDRESS_UPDATED + 1;
    let mailingAddressUpdatedDaysDiff = DAYS_TO_COMPARE_SINCE_ADDRESS_UPDATED + 1;
    const todayDate = format(getDate(), DATE_FORMAT_DB);

    if (rootState.user.current.mailingAddress.updated !== '0000-00-00 00:00:00') {
      const mailingAddressUpdatedDate = format(parseISO(rootState.user.current.mailingAddress.updated), DATE_FORMAT_DB);

      mailingAddressUpdatedDaysDiff = differenceInCalendarDays(new Date(todayDate), new Date(mailingAddressUpdatedDate));
    }

    if (rootState.user.current.lastAddressUpdateDate) {
      const primaryAddressUpdatedDate = format(parseISO(rootState.user.current.lastAddressUpdateDate), DATE_FORMAT_DB);

      primaryAddressUpdatedDaysDiff = differenceInCalendarDays(new Date(todayDate), new Date(primaryAddressUpdatedDate));
    }

    return {mailingAddressUpdatedDaysDiff, primaryAddressUpdatedDaysDiff};
  },
  addressChangedStatus: (_state, _getters) =>
    _getters.addressUpdatedDiff.mailingAddressUpdatedDaysDiff < DAYS_TO_COMPARE_SINCE_ADDRESS_UPDATED ||
    _getters.addressUpdatedDiff.primaryAddressUpdatedDaysDiff < DAYS_TO_COMPARE_SINCE_ADDRESS_UPDATED,
  daysRemainingUntilCheckRequestAvailable: (_state, _getters) => {
    const {mailingAddressUpdatedDaysDiff} = _getters.addressUpdatedDiff;
    const {primaryAddressUpdatedDaysDiff} = _getters.addressUpdatedDiff;

    const minDaysDiff = Math.min(mailingAddressUpdatedDaysDiff, primaryAddressUpdatedDaysDiff);

    return minDaysDiff < DAYS_TO_COMPARE_SINCE_ADDRESS_UPDATED ? DAYS_TO_COMPARE_SINCE_ADDRESS_UPDATED - minDaysDiff : '';
  },
  stateWitholdingData: (_state, _getters, rootState) => rootState.transfers.stateTaxWithholdingData,
};

const actions = {
  setSelectedAccount({commit}, account) {
    commit('SET_SELECTED_ACCOUNT', account);
  },

  selectBankLink({commit}, bankLink) {
    commit('SET_SELECTED_BANK_LINK', bankLink);
    commit('SET_DISTRIBUTION_MODE', DISTRIBUTION_MODE.ACH);
    commit('SET_SELECTED_CHECK_ADDRESS', null);
    commit('SET_SELECTED_ADDRESS_TYPE', null);
    commit('SET_CHECK_PROCESSING_FEE', 0);
    commit('SET_CHECK_DELIVERY_TYPE', null);
  },

  selectCheckAddress({commit}, {address, check}) {
    commit('SET_SELECTED_BANK_LINK', null);
    commit('SET_DISTRIBUTION_MODE', DISTRIBUTION_MODE.CHECK);
    commit('SET_SELECTED_CHECK_ADDRESS', address || null);
    commit('SET_SELECTED_ADDRESS_TYPE', address?.type || null);
    commit('SET_CHECK_PROCESSING_FEE', check.fee);
    commit('SET_CHECK_DELIVERY_TYPE', check.type);
  },

  async setBankLinkVerification({rootGetters, getters: get, commit}) {
    const data = await custodianService.getBankLinkIdentityRequests({
      clUserId: rootGetters['cognitoUser/userId'],
      bankAccountId: get.selectedBankLink.id,
    });

    const approvedStatus = new Set(['APPROVED', 'SYSTEM_APPROVED']);

    // eslint-disable-next-line unicorn/prefer-array-some
    const approved = data.length === 0 ? true : !!data.find(({status}) => approvedStatus.has(status));

    commit('SET_BANK_LINK_VERIFICATION', approved);
  },

  setTaxInformation({commit}, taxInformation) {
    commit('SET_TAX_INFORMATION', taxInformation);
  },
  async getLiquidationLandingContent({state, commit}) {
    try {
      const response = await getSortedContent({
        contentType: state.contentType,
      });
      const liquidationContent = response.filter((content) => content.pageName === 'LiquidationLandingPage');

      commit('setLiquidationLandingContent', liquidationContent);
    } catch {
      logger.addBreadcrumb({
        callingFuncName: 'getLiquidationLandingContent',
        message: `Contentful fetch error caught`,
      });
    }
  },
  // eslint-disable-next-line consistent-return
  async getIraConstraints({commit}, {accountId, distributionMode}) {
    const data = await liquidationService.getIraConstraints({
      accountId,
      withdrawalMechanism: distributionMode,
    });

    if (data) {
      commit('SET_IRA_CONSTRAINTS', data.distributionConstraints);
      commit('SET_IS_RETIREMENT_ACCOUNT', data.retirementAccount);

      return data.distributionConstraints;
    }
  },

  clearLiquidationProgress({commit}) {
    commit('CLEAR_STORE');
  },

  async submitLiquidationRequest({state, getters: get, rootGetters}) {
    const liquidationData = {
      clUserId: Number(rootGetters['cognitoUser/userId']),
      riaUserId: rootGetters['user/riaUserId'],
      accountName: get.selectedAccount.name,
      accountNumber: get.selectedAccount.accountNumber,
      custodian: get.selectedAccount.custodianCode,
      accountId: get.selectedAccount.accountId,
      bankAccountId: state.selectedBankLink ? state.selectedBankLink.id : 0,
      bankName: state.selectedBankLink ? state.selectedBankLink.bankName : null,
      bankAccountNumber: state.selectedBankLink ? state.selectedBankLink.formattedBankAccountNumber : null,
      retirementAccount: get.retirementAccount,
      linkedStatus: state.selectedBankLink ? state.selectedBankLink.linked : false,
      transferType: transferType.createNewInstructions,
      transferDirectionId: TRANSFERDIRECTIONID.WITHDRAWAL,
      transferAmount: Number(get.selectedAccount.balances.current),
      transferFrequency: transferFrequency.oneTime,
      transferStartDate: getDate().toISOString(),
      transferStatus: transferStatus.liquidation,
      fedTaxWithholdingValue: Number.parseFloat(get.taxInformation.federalTax).toFixed(2),
      fedTaxWithholdingValueType: 'PERCENTAGE',
      stateTaxWithholdingValue: Number.parseFloat(get.taxInformation.stateTax).toFixed(2),
      stateTaxWithholdingValueType: get.stateWitholdingData.suffixType === '$' ? 'FIXED' : 'PERCENTAGE',
      distributionReason: get.taxInformation.distributionReason,
      distributionMode: get.distributionMode,
      distributionFee: get.taxInformation.distributionFee,
      addressType: get.selectedAddressType,
      address: get.selectedCheckAddress,
      checkProcessingFee: get.checkProcessingFee,
      checkDeliveryType: get.checkDeliveryType,
      transferInitiatedFromId: TRANSFER_INITIATED_FROM_ID.CLIENT,
    };
    const bccOnLiquidationEmails = rootGetters['settings/bccOnLiquidationEmails'];

    if (!bccOnLiquidationEmails) {
      liquidationData.doNotBccOnAdvisorEmails = true;
    }

    const {data} = await liquidationService.submitLiquidationRequest(liquidationData);

    return data;
  },
};

const mutations = {
  SET_SELECTED_ACCOUNT(state, account) {
    state.selectedAccount = account;
  },
  SET_TAX_INFORMATION(state, taxInformation) {
    state.taxInformation = taxInformation;
  },
  SET_SELECTED_BANK_LINK(state, link) {
    state.selectedBankLink = link;
  },
  SET_DISTRIBUTION_MODE(state, mode) {
    if (!Object.values(DISTRIBUTION_MODE).includes(mode)) {
      throw new Error('Invalid Distribution Mode Selected');
    }

    state.distributionMode = mode;
  },
  SET_SELECTED_ADDRESS_TYPE(state, addressType) {
    state.selectedAddressType = addressType;
  },
  SET_SELECTED_CHECK_ADDRESS(state, address) {
    state.selectedCheckAddress = address;
  },
  SET_CHECK_PROCESSING_FEE(state, fee) {
    state.checkProcessingFee = fee;
  },
  SET_CHECK_DELIVERY_TYPE(state, type) {
    state.checkDeliveryType = type;
  },
  SET_BANK_LINK_VERIFICATION(state, status) {
    state.selectedBankLinkVerified = status;
  },
  SET_IRA_CONSTRAINTS(state, data) {
    state.iraConstraints = data;
  },
  SET_IS_RETIREMENT_ACCOUNT(state, data) {
    state.retirementAccount = data;
  },
  setLiquidationLandingContent(state, liquidationLandingContent) {
    state.liquidationLandingContent = liquidationLandingContent;
  },
  CLEAR_STORE(state) {
    Object.assign(state, initialState);
  },
};

export default {
  namespaced: true,
  state: {...initialState},
  getters,
  actions,
  mutations,
};
